const originalSetTimeout = window.setTimeout;
const originalClearTimeout = window.clearTimeout;

/**
 * This whole thing is a patch for setTimeout using signed32 bit integers. When you pass an integer greater than 2_147_483_647,
 * it will overflow and result in the timeout being called immediately. This monkeypatches setTimeout to allow longer timeouts
 * by chaining them together.
 */

const MAX_DELAY = 2_147_483_647; // Max signed 32 bit integer (~24.8 days max)

const TIMEOUT_MAPPING_PAIRS: {
    original: Record<number, number>;
    patched: Record<number, number>;
} = {
    original: {},
    patched: {},
};

function patchedSetTimeout(...args: Parameters<typeof window.setTimeout>) {
    const [callback, delay, ...rest] = args;
    if (delay && delay > MAX_DELAY) {
        const originalTimeout = originalSetTimeout(
            () => {
                const newTimeout = patchedSetTimeout(
                    callback,
                    delay - MAX_DELAY,
                    ...rest,
                );
                const prevTimeout =
                    TIMEOUT_MAPPING_PAIRS.patched[originalTimeout] ??
                    originalTimeout;
                TIMEOUT_MAPPING_PAIRS.original[prevTimeout] = newTimeout;
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete TIMEOUT_MAPPING_PAIRS.patched[originalTimeout];
                TIMEOUT_MAPPING_PAIRS.patched[newTimeout] = prevTimeout;
                return newTimeout;
            },
            MAX_DELAY,
            ...rest,
        );
        return originalTimeout;
    }

    const timeoutId = originalSetTimeout(
        (...args: Parameters<typeof callback>) => {
            callback(...args);
            const rootTimeoutId = TIMEOUT_MAPPING_PAIRS.patched[timeoutId];
            if (rootTimeoutId !== undefined) {
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete TIMEOUT_MAPPING_PAIRS.original[rootTimeoutId];
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete TIMEOUT_MAPPING_PAIRS.patched[timeoutId];
            }
        },
        delay,
        ...rest,
    );
    return timeoutId;
}

function patchedClearTimeout(...args: Parameters<typeof window.clearTimeout>) {
    const [timeout] = args;
    if (!timeout) return;
    const originalTimeoutId = Number(timeout);
    const activeTimeoutId = TIMEOUT_MAPPING_PAIRS.original[originalTimeoutId];
    if (activeTimeoutId && activeTimeoutId !== originalTimeoutId) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete TIMEOUT_MAPPING_PAIRS.original[originalTimeoutId];
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete TIMEOUT_MAPPING_PAIRS.patched[activeTimeoutId];
        originalClearTimeout(activeTimeoutId);
    } else {
        originalClearTimeout(originalTimeoutId);
    }
}

// @ts-expect-error - patching setTimeout
setTimeout = patchedSetTimeout;
// @ts-expect-error - patching window.setTimeout
window.setTimeout = patchedSetTimeout;
// @ts-expect-error - patching clearTimeout
clearTimeout = patchedClearTimeout;
window.clearTimeout = patchedClearTimeout;
